import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../store";
import { useHomeData, HeroVideo } from "../../homeData";
import { Instagram, ArrowRight, Zap, Battery, Wrench } from "lucide-react";

/* V1 — Cinematic editorial. Liftfoils-inspired. Full-bleed video, large serif typography. */
export default function V1() {
  const { t } = useApp();
  const { products, blog, igPosts } = useHomeData();

  return (
    <main className="bg-white text-slate-900" data-testid="home-v1">
      {/* HERO */}
      <section className="relative h-screen overflow-hidden">
        <HeroVideo overlay="linear-gradient(180deg, rgba(15,23,42,0.4) 0%, rgba(15,23,42,0.15) 50%, rgba(255,255,255,0.0) 100%)" />
        <div className="absolute inset-0 flex flex-col justify-end pb-32">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 fade-up text-white">
            <div className="overline mb-6 text-sky-300">{t.hero.overline}</div>
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] max-w-5xl">{t.hero.title}</h1>
            <p className="font-body text-lg md:text-2xl text-white/90 max-w-2xl mt-8 leading-relaxed font-light">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/products/efoils" className="btn-primary" data-testid="hero-cta-shop">{t.hero.cta1}</Link>
              <Link to="/book" className="btn-ghost !text-white !border-white" data-testid="hero-cta-book">{t.hero.cta2}</Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-hint text-white/80 text-xs font-ui tracking-[0.3em] uppercase">Scroll</div>
      </section>

      {/* USPs - large editorial blocks */}
      <section className="py-32 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="overline mb-3">{t.usps.overline}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl mb-20 max-w-3xl">{t.usps.title}</h2>
        <div className="space-y-24">
          <USPRow num="01" big={t.usps.f1t} title="Lightest in the world" body={t.usps.f1d} stats={[["SharkX", "24 kg"], ["Competitor", "35 kg"], ["Saved", "-11 kg"]]} />
          <div className="divider-thin" />
          <USPRow num="02" big={<Battery size={120} strokeWidth={1} className="text-sky-500" />} title={t.usps.f2t} body={t.usps.f2d} reverse />
          <div className="divider-thin" />
          <USPRow num="03" big={<Wrench size={120} strokeWidth={1} className="text-sky-500" />} title={t.usps.f3t} body={t.usps.f3d} />
        </div>
      </section>

      {/* Product showcase - full-bleed alternating */}
      {products.slice(0, 3).map((p, i) => (
        <section key={p.id} className="relative min-h-[80vh] flex items-center" data-testid={`v1-feature-${p.slug}`}>
          <div className={`max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <div className="aspect-[4/5] overflow-hidden">
              <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div>
              <div className="overline mb-4">{p.category}</div>
              <h3 className="font-serif-display text-5xl md:text-6xl">{p.name}</h3>
              <p className="text-slate-600 mt-5 text-lg italic font-serif-display">{p.tagline}</p>
              <div className="mt-8 flex flex-wrap gap-2">
                {Object.entries(p.specs || {}).slice(0, 4).map(([k, v]) => <span key={k} className="spec-pill">{k.replace(/_/g, " ")}: {v}</span>)}
              </div>
              <Link to={`/product/${p.slug}`} className="btn-primary mt-10" data-testid={`v1-cta-${p.slug}`}>Configure <ArrowRight size={14} /></Link>
            </div>
          </div>
        </section>
      ))}

      <LearnSection t={t} />
      <InstagramSection posts={igPosts} />
      <BlogSection blog={blog} />
    </main>
  );
}

function USPRow({ num, big, title, body, stats, reverse }) {
  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div>
        <div className="overline mb-4">{num}</div>
        {typeof big === "string" ? (
          <div className="font-serif-display text-8xl md:text-[12rem] text-sky-500 leading-none">{big}</div>
        ) : (
          <div className="flex items-center justify-center h-48">{big}</div>
        )}
      </div>
      <div>
        <h3 className="font-serif-display text-4xl md:text-5xl">{title}</h3>
        <p className="text-slate-600 mt-5 text-lg leading-relaxed max-w-md">{body}</p>
        {stats && (
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {stats.map(([k, v]) => (
              <div key={k} className="border-l-2 border-sky-500 pl-3">
                <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
                <div className="font-serif-display text-2xl">{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function LearnSection({ t }) {
  return (
    <section className="relative min-h-[560px] flex items-center mt-20">
      <img src="https://images.unsplash.com/photo-1593547103424-4aa8c049b952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 w-full">
        <div className="max-w-xl">
          <div className="overline mb-4">{t.learn.overline}</div>
          <h2 className="font-serif-display text-5xl md:text-7xl">{t.learn.title}</h2>
          <p className="text-slate-700 mt-6 text-lg">{t.learn.body}</p>
          <Link to="/book" className="btn-primary mt-10" data-testid="home-learn-cta">{t.learn.cta}</Link>
        </div>
      </div>
    </section>
  );
}

export function InstagramSection({ posts }) {
  return (
    <section className="py-32 px-6 md:px-12 max-w-[1440px] mx-auto">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline mb-3">@sharkxelectric</div>
          <h2 className="font-serif-display text-5xl md:text-6xl text-slate-900">From our feed</h2>
        </div>
        <a href="https://www.instagram.com/sharkxelectric" target="_blank" rel="noreferrer" className="flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] hover:text-sky-500 text-slate-700" data-testid="ig-link">
          <Instagram size={16} /> Follow
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="ig-grid">
        {posts.slice(0, 10).map((post, i) => (
          <a key={i} href={post.post_url} target="_blank" rel="noreferrer" className="relative aspect-square overflow-hidden group">
            <img src={post.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-sky-500/0 group-hover:bg-sky-500/20 transition-colors grid place-items-center">
              <Instagram size={26} className="opacity-0 group-hover:opacity-100 text-white transition-opacity" />
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export function BlogSection({ blog }) {
  return (
    <section className="pb-32 px-6 md:px-12 max-w-[1440px] mx-auto">
      <div className="overline mb-3">Journal</div>
      <h2 className="font-serif-display text-5xl md:text-6xl mb-12 text-slate-900">Stories &amp; guides</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {blog.map((b) => (
          <Link to={`/blog/${b.slug}`} key={b.id} className="group card-clean overflow-hidden block" data-testid={`blog-${b.slug}`}>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={b.cover} alt={b.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="p-6">
              <div className="overline mb-2">{b.author}</div>
              <div className="font-serif-display text-2xl md:text-3xl leading-tight group-hover:text-sky-500 transition-colors text-slate-900">{b.title}</div>
              <div className="text-slate-600 mt-3 text-sm">{b.excerpt}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
